import React, { useState, useEffect } from 'react'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Popper from '@material-ui/core/Popper'
import PropTypes from 'prop-types'
import L from '../L'
import Type from '../Type'
import IconSearch from '../../Icons/IconSearch'
import IconClose from '../../Icons/IconMXClose'
import IconCollection from '../../Icons/IconCollection'
import IconFilter from '../../Icons/IconFilter'
import IconTag from '../../Icons/IconTag'
import IconSim from '../../Icons/IconSim'
import StyledSearch from './Search-styles'

const defaultOptions = []

const createOptions = (collections, simulations, remove) => {
  const options = !remove.includes('Filters') ? [...defaultOptions] : []

  if (!remove.includes('Collections')) {
    Object.values(collections).map(collection => {
      if (collection.type === 'event' || collection.type === 'vertical') {
        options.push({
          key: collection.id,
          title: collection.name,
          groupLabel: 'Collections',
        })
      }
    })
  }

  const simIds = []
  const simNames = []
  if (!remove.includes('Simulations')) {
    Object.values(simulations).map(sim => {
      if (
        !simIds.includes(sim.id) &&
        !simNames.includes(sim.name) &&
        !sim.hasOwnProperty('marketingExp') &&
        !sim.hasOwnProperty('hideProd')
      ) {
        simIds.push(sim.id)
        simNames.push(sim.name)
        options.push({
          key: sim.id,
          title: sim.name,
          groupLabel: 'Simulation Names',
        })
      }
    })
  }

  const tags = new Set()
  const tagsArr = []

  if (!remove.includes('Tags')) {
    for (const prop in simulations) {
      const sim = simulations[prop]
      if (sim.tags && sim.tags.length > 0) sim.tags.forEach(key => tags.add(key))
    }
    const temp = Array.from(tags)
    for (let i = 0; i < temp.length; i++) {
      tagsArr.push({ key: temp[i], title: temp[i], groupLabel: 'Tags' })
    }
  }

  return options.concat(tagsArr)
}

const Search = ({
  collections,
  simulations,
  onChange,
  remove = [],
  value = [],
  fluid = false,
  onToggle,
  animationState,
  placeholder,
}) => {
  const [options, setOptions] = useState(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    if (!options) setOptions(createOptions(collections, simulations, remove))
  }, [collections, simulations])

  let defaultValue = []
  useEffect(() => {
    defaultValue = value
  }, [])

  if (!options) return null

  return (
    <StyledSearch animationState={animationState} fluid={fluid}>
      <L
        display="grid"
        gap={0.75}
        flow="column"
        ac="center"
        selectable
        onMouseUp={() => {
          onToggle(true)
          setTimeout(() => {
            document.getElementById('search').focus()
          }, 650)
        }}
      >
        <IconSearch color={animationState === 'turn-on' || animationState === 'set-on' ? '#E11C8E' : '#033691'} />
      </L>
      <Autocomplete
        id="search"
        // debug
        onChange={(e, searchItems, reason) => {
          onChange(e, searchItems, reason)
        }}
        onClose={(e, reason) => {
          if (reason === 'blur' || reason === 'escape') setDropdownOpen(false)
          if (reason === 'blur' && !value.length) onToggle(false)
        }}
        autoHighlight
        defaultValue={defaultValue}
        open={dropdownOpen}
        multiple
        size="small"
        options={options.sort((a, b) => -b.groupLabel.localeCompare(a.groupLabel))}
        groupBy={option => option.groupLabel}
        getOptionLabel={option => option.title}
        style={{ width: '100%' }}
        closeIcon={<IconClose />}
        noOptionsText="Type to search collections, filters, and tags"
        autoComplete
        filterSelectedOptions
        fullWidth
        onFocus={() => setDropdownOpen(true)}
        // openOnFocus
        PopperComponent={params => {
          return <Popper {...params} placement="bottom-start" transition />
        }}
        ChipProps={{
          size: 'small',
          deleteIcon: (
            <L display="flex" height="24px" width="16px" mL={-0.2} mR={0.6}>
              <IconClose />
            </L>
          ),
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              endAdornment: dropdownOpen && (
                <InputAdornment
                  position="end"
                  onMouseUp={() => {
                    setDropdownOpen(false)
                    if (!value.length) onToggle(false)
                    document.getElementById('search').blur()
                  }}
                >
                  <Type variant="input" color="primaryHue" selectable>
                    Close
                  </Type>
                </InputAdornment>
              ),
            }}
          />
        )}
        renderGroup={params => [
          <div key={params.key}>
            <L display="grid" flow="column" ai="center" jc="flex-start" pX={2} pY={1} gap={0.5}>
              {params.group === 'Simulation Names' ? (
                <IconSim />
              ) : params.group === 'Filters' ? (
                <IconFilter />
              ) : params.group === 'Collections' ? (
                <IconCollection />
              ) : params.group === 'Tags' ? (
                <IconTag />
              ) : null}
              <Type variant="body2" color="tertiary" upper>
                {params.group}
              </Type>
            </L>
          </div>,
          params.children,
        ]}
        renderOption={(option, state) => (
          <div className={state.selected ? 'selected' : ''}>
            <Type variant="h5" color="secondaryHueLight">
              {option.title}
            </Type>
          </div>
        )}
      />
    </StyledSearch>
  )
}

Search.propTypes = {
  collections: PropTypes.array,
  simulations: PropTypes.object,
  placeholder: PropTypes.string,
}

Search.defaultProps = {
  collections: [],
  simulations: {},
}

export default Search
